import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const typeFilters = ['All', 'Daily', 'Weekly', 'Monthly'];

function CurrentReports() {
  const user =
    localStorage.getItem('username') ||
    sessionStorage.getItem('username') ||
    'demo';

  const [reports, setReports] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [toast, setToast] = useState(null);
  const [focusReport, setFocusReport] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/get_reports.php?username=${encodeURIComponent(user)}`);
      const data = await res.json();
      setReports(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const res = await fetch(`/get_user_devices.php?username=${encodeURIComponent(user)}`);
        const data = await res.json();
        setDevices(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
        setDevices([]);
      }
    };

    fetchDevices();
  }, [user]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3500);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const filteredReports = useMemo(() => {
    const matcher = search.toLowerCase();
    return reports.filter((report) => {
      const reportName = (report.report_name || '').toLowerCase();
      const deviceName = (report.device || '').toLowerCase();
      const unitName = (report.unit || '').toLowerCase();
      const matchesSearch =
        !matcher ||
        reportName.includes(matcher) ||
        deviceName.includes(matcher) ||
        unitName.includes(matcher);
      const matchesType = typeFilter === 'All' || report.report_type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [reports, search, typeFilter]);

  const openEditModal = (report) => {
    setEditModal({
      ...report,
      report_active: Number(report.report_active) === 1,
      scheduled_time: report.scheduled_time ? report.scheduled_time.slice(0, 5) : '',
      assigned_to: report.assigned_to
        ? report.assigned_to
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
        : [],
    });
    setEmailDraft('');
  };

  const updateEditField = (field, value) => {
    setEditModal((prev) => ({ ...prev, [field]: value }));
  };

  const updateEditDevice = (deviceName) => {
    const deviceObj = devices.find((d) => d.device_name === deviceName);
    setEditModal((prev) => ({
      ...prev,
      device: deviceName,
      unit: deviceObj ? deviceObj.unit : prev.unit,
    }));
  };

  const addEditRecipient = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = emailDraft.trim();
      if (!trimmed) return;
      if (editModal.assigned_to.includes(trimmed)) {
        setToast({ message: 'Recipient already exists.', success: false });
        return;
      }
      setEditModal((prev) => ({
        ...prev,
        assigned_to: [...prev.assigned_to, trimmed],
      }));
      setEmailDraft('');
    }
  };

  const removeEditRecipient = (index) => {
    setEditModal((prev) => ({
      ...prev,
      assigned_to: prev.assigned_to.filter((_, i) => i !== index),
    }));
  };

  const toggleActive = async (report) => {
    if (togglingId) return;
    const newValue = Number(report.report_active) === 1 ? 0 : 1;
    setTogglingId(report.id);
    try {
      const res = await fetch('/update_report.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: Number(report.id),
          username: user,
          report_active: newValue,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update report');
      }
      setReports((prev) =>
        prev.map((item) =>
          item.id === report.id ? { ...item, report_active: newValue } : item
        )
      );
      setToast({
        message: `Report ${newValue === 1 ? 'activated' : 'paused'} successfully.`,
        success: true,
      });
    } catch (error) {
      console.error(error);
      setToast({ message: 'Unable to update status. Please retry.', success: false });
    } finally {
      setTogglingId(null);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editModal) return;
    setIsSavingEdit(true);
    try {
      const payload = {
        id: Number(editModal.id),
        username: user,
        report_name: editModal.report_name,
        device: editModal.device,
        unit: editModal.unit,
        report_type: editModal.report_type,
        scheduled_time: editModal.scheduled_time,
        assigned_to: editModal.assigned_to.join(','),
        report_active: editModal.report_active ? 1 : 0,
      };
      const res = await fetch('/update_report.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update report');
      }
      setToast({ message: 'Report updated successfully.', success: true });
      setEditModal(null);
      fetchReports();
    } catch (error) {
      console.error(error);
      setToast({ message: 'Could not save changes. Please try again.', success: false });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    try {
      const res = await fetch('/delete_report.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(deleteCandidate.id), username: user }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to delete report');
      }
      setToast({ message: 'Report removed successfully.', success: true });
      setDeleteCandidate(null);
      fetchReports();
    } catch (error) {
      console.error(error);
      setToast({ message: 'Unable to remove report. Please try again.', success: false });
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden text-white">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#151C2A] via-[#0C1017] to-[#080B10]" />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 0.45, scale: 1 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="absolute left-4 top-16 h-72 w-72 rounded-full bg-primary/30 blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 0.35, scale: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 }}
          className="absolute bottom-10 right-10 h-80 w-80 rounded-full bg-sky-500/30 blur-3xl"
        />
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed right-6 top-6 z-50 flex items-center gap-3 rounded-full border px-5 py-3 text-sm shadow-xl ${
              toast.success
                ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-200'
                : 'border-rose-500/60 bg-rose-500/15 text-rose-200'
            }`}
          >
            <span className="text-lg">{toast.success ? '✅' : '⚠️'}</span>
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative mx-auto flex w-full max-w-6xl flex-col gap-10 px-6 py-16">
        <header className="flex flex-col gap-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-white/60">Reports</p>
              <h1 className="text-4xl font-semibold">Current schedules</h1>
              <p className="max-w-xl text-sm text-white/65">
                Review, edit and manage your automated report schedules. Toggle activity, adjust recipients and keep your
                teams aligned.
              </p>
            </div>
            <div className="flex flex-col items-end gap-3 text-xs text-white/60">
              <span className="rounded-full border border-white/10 bg-white/5 px-4 py-2 uppercase tracking-[0.3em]">
                {reports.length} total
              </span>
              <span className="flex items-center gap-2 text-white/50">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-300" />
                </span>
                Auto-sync enabled
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-4 rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-sm">
              <input
                type="search"
                placeholder="Search by name, device or unit"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none"
              />
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-white/40">⌕</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {typeFilters.map((filter) => {
                const active = typeFilter === filter;
                return (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => setTypeFilter(filter)}
                    className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] transition ${
                      active
                        ? 'border-transparent bg-gradient-to-r from-primary/80 to-sky-500/70 text-white shadow-lg shadow-primary/20'
                        : 'border-white/10 bg-white/5 text-white/60 hover:border-primary/40 hover:text-white'
                    }`}
                  >
                    {filter}
                  </button>
                );
              })}
            </div>
          </div>
        </header>

        <section className="space-y-4">
          <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/5 shadow-[0px_20px_45px_-35px_rgba(15,102,241,0.55)] backdrop-blur">
            <div className="hidden grid-cols-[1.5fr_1fr_0.9fr_1.4fr_auto_auto] border-b border-white/5 px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white/40 md:grid">
              <span>Report</span>
              <span>Device</span>
              <span>Cadence</span>
              <span>Recipients</span>
              <span>Status</span>
              <span className="text-right">Actions</span>
            </div>
            <div className="divide-y divide-white/5">
              {loading &&
                Array.from({ length: 5 }).map((_, idx) => (
                  <div
                    key={`skeleton-${idx}`}
                    className="grid gap-4 px-6 py-5 text-sm md:grid-cols-[1.5fr_1fr_0.9fr_1.4fr_auto_auto]"
                  >
                    <div className="h-4 w-3/4 animate-pulse rounded-full bg-white/10" />
                    <div className="h-4 w-2/3 animate-pulse rounded-full bg-white/10" />
                    <div className="h-4 w-1/2 animate-pulse rounded-full bg-white/10" />
                    <div className="h-4 w-4/5 animate-pulse rounded-full bg-white/10" />
                    <div className="h-9 w-20 animate-pulse rounded-full bg-white/10" />
                    <div className="flex items-center justify-end gap-2">
                      <span className="h-8 w-16 animate-pulse rounded-full bg-white/10" />
                      <span className="h-8 w-16 animate-pulse rounded-full bg-white/10" />
                    </div>
                  </div>
                ))}

              {!loading &&
                filteredReports.map((report, idx) => {
                  const isActive = Number(report.report_active) === 1;
                  const recipients = report.assigned_to
                    ? report.assigned_to
                        .split(',')
                        .map((email) => email.trim())
                        .filter(Boolean)
                    : [];
                  const displayRecipients = recipients.slice(0, 3);
                  const remainingRecipients = recipients.length - displayRecipients.length;

                  return (
                    <motion.div
                      key={report.id}
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.04, duration: 0.35, ease: 'easeOut' }}
                      className="grid gap-4 px-6 py-5 text-sm text-white/70 md:grid-cols-[1.5fr_1fr_0.9fr_1.4fr_auto_auto] md:items-center"
                    >
                      <div className="space-y-1">
                        <p className="text-base font-semibold text-white">{report.report_name}</p>
                        <p className="text-[0.65rem] uppercase tracking-[0.35em] text-white/45">
                          {report.unit || 'No unit defined'}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <p className="text-sm text-white">{report.device || 'No device selected'}</p>
                        <p className="text-[0.65rem] uppercase tracking-[0.3em] text-white/40">Linked device</p>
                      </div>

                      <div className="space-y-1">
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs text-white/80">
                          <span>{report.report_type}</span>
                          <span className="text-white/40">·</span>
                          <span>{report.scheduled_time?.slice(0, 5) || 'Pending'}</span>
                        </div>
                        <p className="text-[0.65rem] uppercase tracking-[0.3em] text-white/40">Cadence</p>
                      </div>

                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-2">
                          {displayRecipients.length > 0 ? (
                            <>
                              {displayRecipients.map((email) => (
                                <span
                                  key={email}
                                  className="rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[0.65rem] text-white/70"
                                >
                                  {email}
                                </span>
                              ))}
                              {remainingRecipients > 0 && (
                                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[0.65rem] text-white/60">
                                  +{remainingRecipients} more
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-white/40">No recipients assigned</span>
                          )}
                        </div>
                        <p className="text-[0.65rem] uppercase tracking-[0.3em] text-white/35">Delivery list</p>
                      </div>

                      <div className="md:justify-self-center">
                        <button
                          type="button"
                          onClick={() => toggleActive(report)}
                          disabled={togglingId === report.id}
                          className={`relative inline-flex h-10 w-24 items-center rounded-full border px-2 text-[0.65rem] font-semibold uppercase tracking-[0.35em] transition ${
                            isActive
                              ? 'border-emerald-400/70 bg-emerald-400/15 text-emerald-200'
                              : 'border-white/10 bg-white/5 text-white/60'
                          } ${togglingId === report.id ? 'animate-pulse' : ''}`}
                        >
                          <span
                            className={`absolute left-1 top-1 h-8 w-8 rounded-full bg-white transition-all duration-300 ${
                              isActive
                                ? 'translate-x-[3.6rem] bg-emerald-400 shadow-lg shadow-emerald-500/40'
                                : ''
                            }`}
                          />
                          <span className="w-full text-center">{isActive ? 'Active' : 'Paused'}</span>
                        </button>
                      </div>

                      <div className="flex flex-wrap justify-end gap-2 text-xs font-semibold uppercase tracking-[0.3em]">
                        <button
                          type="button"
                          onClick={() => setFocusReport(report)}
                          className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-white/70 transition hover:border-primary/60 hover:text-white"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditModal(report)}
                          className="rounded-full border border-primary/50 bg-primary/60 px-4 py-2 text-white transition hover:bg-primary/80"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteCandidate(report)}
                          className="rounded-full border border-rose-500/60 bg-rose-500/20 px-4 py-2 text-rose-100 transition hover:bg-rose-500/30"
                        >
                          Remove
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
            </div>
          </div>

          {!loading && filteredReports.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-white/15 bg-white/5 p-12 text-center text-sm text-white/60">
              <span className="text-4xl">🗂️</span>
              <p>No reports match your filters just yet.</p>
              <p className="max-w-md text-white/40">
                Try adjusting the search keywords or cadence filter. New reports appear here instantly after creation.
              </p>
            </div>
          )}
        </section>
      </div>

      <AnimatePresence>
        {focusReport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur"
            onClick={() => setFocusReport(null)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-xl rounded-3xl border border-white/10 bg-[#10141D] p-8 text-sm text-white/70 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setFocusReport(null)}
                className="absolute right-5 top-5 text-xl text-white/40 transition hover:text-white"
                aria-label="Close"
              >
                ×
              </button>
              <h3 className="text-2xl font-semibold text-white">{focusReport.report_name}</h3>
              <p className="text-xs uppercase tracking-[0.3em] text-white/40">#{focusReport.id}</p>
              <div className="mt-6 space-y-3">
                {Object.entries(focusReport).map(([key, value]) => (
                  <div key={key} className="flex items-start justify-between gap-4">
                    <span className="text-white/50 capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="max-w-xs text-right text-white">
                      {key === 'report_active'
                        ? Number(value) === 1
                          ? 'Active'
                          : 'Inactive'
                        : value || '—'}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur"
          >
            <motion.form
              onSubmit={submitEdit}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-2xl space-y-6 rounded-3xl border border-white/10 bg-[#10141D] p-8 text-sm text-white"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-2xl font-semibold">Edit report</h3>
                  <p className="text-xs text-white/60">Adjust cadence, recipients or delivery details.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditModal(null)}
                  className="text-xl text-white/40 transition hover:text-white"
                >
                  ×
                </button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 text-xs">
                  <span className="text-white/60">Report name</span>
                  <input
                    type="text"
                    value={editModal.report_name}
                    onChange={(e) => updateEditField('report_name', e.target.value)}
                    required
                    className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none"
                  />
                </label>
                <label className="flex flex-col gap-2 text-xs">
                  <span className="text-white/60">Cadence</span>
                  <select
                    value={editModal.report_type}
                    onChange={(e) => updateEditField('report_type', e.target.value)}
                    className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white focus:border-primary/60 focus:outline-none"
                  >
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                  </select>
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 text-xs">
                  <span className="text-white/60">Device</span>
                  <select
                    value={editModal.device}
                    onChange={(e) => updateEditDevice(e.target.value)}
                    className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white focus:border-primary/60 focus:outline-none"
                  >
                    <option value="">Select device</option>
                    {devices.map((device) => (
                      <option key={device.device_name} value={device.device_name} className="bg-[#0C1017] text-white">
                        {device.device_name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-2 text-xs">
                  <span className="text-white/60">Unit</span>
                  <input
                    type="text"
                    value={editModal.unit || ''}
                    onChange={(e) => updateEditField('unit', e.target.value)}
                    className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white focus:border-primary/60 focus:outline-none"
                  />
                </label>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-2 text-xs">
                  <span className="text-white/60">Scheduled time</span>
                  <input
                    type="time"
                    value={editModal.scheduled_time}
                    onChange={(e) => updateEditField('scheduled_time', e.target.value)}
                    className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white focus:border-primary/60 focus:outline-none"
                  />
                </label>
                <label className="flex items-center gap-3 text-xs text-white/60">
                  <input
                    type="checkbox"
                    checked={editModal.report_active}
                    onChange={(e) => updateEditField('report_active', e.target.checked)}
                    className="h-5 w-5 rounded border border-white/20 bg-black/40 accent-emerald-400"
                  />
                  Report active
                </label>
              </div>

              <div className="space-y-3">
                <p className="text-xs text-white/60">Recipients</p>
                <div className="flex flex-wrap gap-2">
                  {editModal.assigned_to.map((email, idx) => (
                    <span
                      key={email}
                      className="flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-[0.65rem] text-white/70"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => removeEditRecipient(idx)}
                        className="text-white/50 transition hover:text-white"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="email"
                  placeholder="Press enter to add"
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                  onKeyDown={addEditRecipient}
                  className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 text-xs font-semibold uppercase tracking-[0.3em]">
                <button
                  type="button"
                  onClick={() => setEditModal(null)}
                  className="rounded-full border border-white/10 bg-white/5 px-6 py-3 text-white/70 transition hover:border-white/30 hover:text-white"
                >
                  Cancel
                </button>
                <motion.button
                  type="submit"
                  whileHover={{ scale: isSavingEdit ? 1 : 1.02 }}
                  whileTap={{ scale: isSavingEdit ? 1 : 0.98 }}
                  disabled={isSavingEdit}
                  className={`rounded-full px-6 py-3 text-white transition ${
                    isSavingEdit
                      ? 'cursor-not-allowed border border-white/10 bg-white/10 text-white/60'
                      : 'border border-transparent bg-gradient-to-r from-primary via-sky-500 to-indigo-500 shadow-lg shadow-primary/20'
                  }`}
                >
                  {isSavingEdit ? 'Saving…' : 'Save changes'}
                </motion.button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {deleteCandidate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md space-y-5 rounded-3xl border border-rose-500/50 bg-[#1A1013] p-8 text-center text-sm text-white"
            >
              <h3 className="text-2xl font-semibold text-white">Remove report?</h3>
              <p className="text-white/60">
                “{deleteCandidate.report_name}” will no longer send automated updates. This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3 text-xs font-semibold uppercase tracking-[0.3em]">
                <button
                  type="button"
                  onClick={() => setDeleteCandidate(null)}
                  className="rounded-full border border-white/10 bg-white/5 px-6 py-3 text-white/70 transition hover:border-white/30 hover:text-white"
                >
                  Keep report
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="rounded-full border border-rose-500/60 bg-rose-500/40 px-6 py-3 text-rose-100 transition hover:bg-rose-500/60"
                >
                  Remove
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default CurrentReports;
